// react
import { Link as RouterLink } from 'react-router-dom';
// framer
import { m } from 'framer-motion';
// @mui
import { styled, useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
// components
import Image from '../../components/Image';
import Iconify from '../../components/Iconify';
import { MotionViewport, varFade } from '../../components/animate';
// routes
import { PATH } from '../../routes/paths';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingDown: theme.spacing(12),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(12, 0),
  },
}));

// ----------------------------------------------------------------------

const ScreenStyle = styled(m.div)(({ theme }) => ({
  paddingRight: 1.5,
  paddingTop: 1,
  marginTop: 150,
  marginBottom: 100,
  maxWidth: 350,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 300 : 800],
  [theme.breakpoints.up('sm')]: {
    maxWidth: 320,
    paddingRight: 2,
    borderRadius: 6,
  },
  '& img': {
    borderRadius: 4,
    [theme.breakpoints.up('sm')]: {
      borderRadius: 6,
    },
  },
}));

const COMMON = {
  scaleX: 1.7,
  skewY: 2,
  skewX: 0,
  scaleY: 1.7,
  translateX: 0,
  translateY: 0,
  opacity: 0,
};

const variantScreenLeft = {
  initial: COMMON,
  animate: { ...COMMON, translateX: '-10%', translateY: 80, opacity: 1 },
};

const variantScreenCenter = {
  initial: COMMON,
  animate: { ...COMMON, opacity: 1, translateX: '20%', translateY: -30 },
};

// ----------------------------------------------------------------------

export default function HomeLookingFor() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const screenLeftAnimate = variantScreenLeft;
  const screenCenterAnimate = variantScreenCenter;
  const isRTL = theme.direction === 'rtl';
  return (
    <RootStyle>
      <div id="dratio-home-minimal">
        <Container component={MotionViewport}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            spacing={{ xs: 8, md: 3 }}
          >
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <m.div variants={varFade().inDown}>
                <Typography variant="overline" component="div" sx={{ color: 'text.disabled' }}>
                  Data as a service
                </Typography>
              </m.div>

              <m.div variants={varFade().inDown}>
                <Typography variant="h2" sx={{ mt: 2, mb: 5 }}>
                  Enrich
                </Typography>
              </m.div>
              <m.div variants={varFade().inUp}>
                <Typography
                  sx={{
                    mb: 5,
                    color: isLight ? 'text.secondary' : 'common.white',
                  }}
                >
                  A wide variety of clean and interconnected datasets ready to enrich your data
                </Typography>
              </m.div>

              <m.div variants={varFade().inDown}>
                <Button
                  color="inherit"
                  size="large"
                  variant="outlined"
                  to={PATH.marketplace.root}
                  component={RouterLink}
                  endIcon={<Iconify icon="ic:round-arrow-right-alt" />}
                >
                  Visit Marketplace
                </Button>
              </m.div>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  justifyContent: 'center',
                }}
              >
                {/* <Box
              component={m.div}
              variants={varFade().inUp}
              sx={{
                mb: { xs: 3, md: 0 },
              }}
            > */}
                <ScreenStyle
                  variants={screenLeftAnimate}
                  transition={{ duration: 0.72, ease: 'easeOut' }}
                  sx={{
                    zIndex: 3,
                    position: 'absolute',
                    boxShadow: `${isRTL ? -80 : 80}px 40px 80px ${alpha(
                      isLight ? theme.palette.grey[600] : theme.palette.common.black,
                      0.6
                    )}`,
                  }}
                >
                  <Image
                    disabledEffect
                    alt="rocket"
                    src={
                      isLight
                        ? 'assets/marketplace/marketplace_3_light.png'
                        : 'assets/marketplace/marketplace_3_dark.png'
                    }
                  />
                </ScreenStyle>
                <ScreenStyle
                  variants={screenCenterAnimate}
                  transition={{ duration: 0.72, ease: 'easeOut' }}
                  sx={{
                    boxShadow: `${isRTL ? -80 : 80}px 40px 80px ${alpha(
                      isLight ? theme.palette.grey[600] : theme.palette.common.black,
                      0.6
                    )}`,
                  }}
                >
                  <Image
                    disabledEffect
                    alt="rocket"
                    src="assets/marketplace/marketplace_1_dark.jpeg"
                  />
                </ScreenStyle>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </div>
    </RootStyle>
  );
}
